import StepsHeroSection from "../components/landing/StepsHeroSection"
import FilingChecklistSection from "../components/landing/FilingChecklistSection"
import FeeScheduleSection from "../components/landing/FeeScheduleSection"
import LandingTextSection from "../components/landing/LandingTextSection"
import InstantFilingSection from "../components/landing/InstantFilingSection"
import Footer from "../components/ui/Footer"
import { useSiteContent } from "../hooks/useSiteContent"

const DEFAULT_STEPS = [
  {
    title: "Application information",
    text: "Provide the applicant's name and postal address.",
  },
  {
    title: "Patent specification",
    text: "Upload your patent specification in English, French or German.",
    bullets: ["Description", "Claims", "Drawings", "Summary"],
  },
  {
    title: "Inventor",
    text: "Provide the required inventor information.",
  },
  {
    title: "Additional requests",
    text: "Provide any additional requests relating to your European patent application.",
  },
]

const DEFAULT_SERVICE_CARDS = [
  {
    title: "Trademark",
    image: "/images/trademark.svg",
    text: "Protect your brand throughout the European Union.",
    path: "/trademark",
  },
  {
    title: "Design",
    image: "/images/design.svg",
    text: "Protect the appearance of your products throughout the European Union.",
    path: "/design",
  },
  {
    title: "Patent",
    image: "/images/patent.svg",
    text: "Protect your technical inventions through the European patent system.",
    path: "/patent",
  },
]

export default function PatentPage() {
  const site = useSiteContent("patent")

  const steps = DEFAULT_STEPS.map((step, index) => ({
    ...step,
    title: site.get(`patent.hero.step.${index}.title`, step.title),
    text: site.get(`patent.hero.step.${index}.text`, step.text),
  }))

  const serviceCards = DEFAULT_SERVICE_CARDS.map((card, index) => ({
    ...card,
    title: site.get(`patent.services.card.${index}.title`, card.title),
    text: site.get(`patent.services.card.${index}.text`, card.text),
  }))

  return (
    <div className="flex min-h-screen flex-col">
      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode ? "bg-orange-500 text-white" : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <StepsHeroSection
        backgroundImage="/images/patent.svg"
        heading={site.get("patent.hero.heading", "EUROPEAN PATENT")}
        description={site.get(
          "patent.hero.description",
          "File your European patent application online without managing the European filing process yourself. Provide your application information and patent specification. xpress-ip prepares and submits the application and provides professional representation before the European Patent Office."
        )}
        steps={steps}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("patent.hero.heading", value)}
        onSaveDescription={(value) => site.save("patent.hero.description", value)}
        onSaveStepTitle={(index, value) => site.save(`patent.hero.step.${index}.title`, value)}
        onSaveStepText={(index, value) => site.save(`patent.hero.step.${index}.text`, value)}
      />

      <FilingChecklistSection
        title={site.get("patent.checklist.title", "FILING CHECKLIST")}
        items={[
          site.get(
            "patent.checklist.item.0",
            "Applicant information: name and postal address"
          ),
          {
            text: site.get(
              "patent.checklist.item.1",
              "Patent specification in English, French or German"
            ),
            bullets: ["Description", "Claims", "Drawings", "Summary"],
          },
          site.get(
            "patent.checklist.item.2",
            "Inventor information: information concerning the inventor(s)"
          ),
          site.get(
            "patent.checklist.item.3",
            "Additional requests: any additional requests relevant to your application"
          ),
        ]}
        editable={site.editMode}
        onSaveTitle={(value) => site.save("patent.checklist.title", value)}
        onSaveItemText={(index, value) => site.save(`patent.checklist.item.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("patent.office.heading", "EUROPEAN PATENT OFFICE")}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("patent.office.heading", value)}
        body={[
          site.get(
            "patent.office.body.0",
            "Your application is submitted to the European Patent Office and professionally represented in cooperation with DANTZ IP & LAW."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`patent.office.body.${index}`, value)}
      />

      <FeeScheduleSection
        title={site.get("patent.fees.title", "TRANSPARENT EUROPEAN PATENT FILING COSTS")}
        description={site.get(
          "patent.fees.description",
          "Review the applicable service fees and official filing fees before submitting your application. Official fees are paid after receipt of your payment."
        )}
        editable={site.editMode}
        onSaveTitle={(value) => site.save("patent.fees.title", value)}
        onSaveDescription={(value) => site.save("patent.fees.description", value)}
      />

      <LandingTextSection
        heading={site.get("patent.notice.heading", "IMPORTANT NOTICE")}
        subheading={site.get(
          "patent.notice.subheading",
          "A streamlined filing service, not an automatic legal workflow"
        )}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("patent.notice.heading", value)}
        onSaveSubheading={(value) => site.save("patent.notice.subheading", value)}
        body={[
          site.get(
            "patent.notice.body.0",
            "xpress-ip is designed to simplify the administrative process of filing your European patent application."
          ),
          site.get(
            "patent.notice.body.1",
            "No additional cost-generating services are automatically performed."
          ),
          site.get(
            "patent.notice.body.2",
            "If you require additional legal or strategic assistance, this can be discussed and agreed separately."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`patent.notice.body.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("patent.why.heading", "WHY XPRESS-IP?")}
        subheading={site.get(
          "patent.why.subheading",
          "From your application to the EPO, without the usual administrative burden"
        )}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("patent.why.heading", value)}
        onSaveSubheading={(value) => site.save("patent.why.subheading", value)}
        body={[
          site.get("patent.why.body.0", "You provide the information and documents."),
          site.get(
            "patent.why.body.1",
            "We prepare the filing documents, submit the application, pay the official fees after receiving your payment, provide a filing report and monitor the relevant deadlines."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`patent.why.body.${index}`, value)}
      />

      <InstantFilingSection
        heading={site.get("patent.services.heading", "INTELLECTUAL PROPERTY RIGHTS")}
        types={serviceCards}
        closingDivider={false}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("patent.services.heading", value)}
        onSaveCardTitle={(index, value) => site.save(`patent.services.card.${index}.title`, value)}
        onSaveCardText={(index, value) => site.save(`patent.services.card.${index}.text`, value)}
      />

      <Footer
        editable={site.editMode}
        email={site.get("patent.footer.email", "info@xpress-ip.com")}
        tagline={site.get("patent.footer.tagline", "EUROPEAN IP FILING, MADE SIMPLE.")}
        taglineSub={site.get("patent.footer.taglineSub", "Patents · Trademarks · Designs")}
        onSaveEmail={(value) => site.save("patent.footer.email", value)}
        onSaveTagline={(value) => site.save("patent.footer.tagline", value)}
        onSaveTaglineSub={(value) => site.save("patent.footer.taglineSub", value)}
      />
    </div>
  )
}
